import type { PublicCourse } from '../../../services/publicService';

/**
 * The IEB range on /ieb is a CURATED topic map (IEB_PAGE.map in content.ts),
 * not a live pull — the landing page makes the same choice for its own twelve
 * topics. A raw course list advertises exactly how much of the range is
 * published on any given day; the map states the syllabus, and the catalogue
 * is one click away.
 *
 * What stays live is the linking: a topic row resolves to its course the day
 * that course is published, and renders as plain text until then — the same
 * rule the landing's topic table follows ("a topic that isn't published yet
 * should still be listed").
 *
 * The `IEB ` title prefix is the board boundary: it keeps this matching away
 * from the NSC catalogue (whose own windows match their own prefixes), so a
 * map entry like "The End of the Cold War" can never resolve to the NSC
 * Study Lab of the same name.
 */

/**
 * Is this title part of the IEB range?
 *
 * Titles, not types: bundles are named by the same rule as courses ("IEB The
 * Full Set", "IEB Skills Lab"), so this is the one board marker for both.
 * The landing page's bundle window relies on it to keep IEB packs off the
 * NSC shelf — that window fetches every published bundle in the tenant, so
 * without a filter the first IEB pack published appears under "Buy by the
 * paper" beside the NSC ones.
 */
export const isIebTitle = (title: string): boolean =>
  title.trim().toUpperCase().startsWith('IEB ');

/**
 * Is this course an IEB STUDY Lab specifically?
 *
 * The shelf under the topic map exists to prove that map, and the map is the
 * Study Labs — so the shelf has to be Study Labs. It used to take the first
 * few IEB courses by title, which was indistinguishable from correct while
 * the Study Labs were the only ones published: the moment the Essay Labs went
 * live for launch they sorted first ("IEB Essay Lab:" before "IEB Study Lab")
 * and the Study Labs section illustrated itself with three Essay Labs.
 */
export const isIebStudyLab = (title: string): boolean =>
  /^IEB\s+Study\s+Lab\b/i.test(title.trim());

/**
 * The published IEB course a map topic points at, or null while it isn't
 * published yet. `match` is a distinctive fragment of the course title,
 * chosen per topic in IEB_PAGE.map — matched case-insensitively, and only
 * within the IEB range.
 *
 * It takes a LIST of candidates because the map's topic names come from the
 * printed flyer while the courses are named by whoever built them, and the
 * two do not always use the same words for the same topic — the flyer says
 * "The Truth and Reconciliation Commission" where the course is "The TRC",
 * and "The Congo and Tanzania" where the course is "Independent Africa -
 * Tanzania". Candidates are tried in order, so a rename on either side is a
 * one-line addition here rather than a chip that silently stops linking.
 */
export const findIebCourse = (
  match: string | readonly string[],
  courses: ReadonlyArray<Pick<PublicCourse, 'id' | 'title'>>,
): { id: string; title: string } | null => {
  const candidates = (typeof match === 'string' ? [match] : match)
    .map((m) => m.trim().toLowerCase())
    .filter(Boolean);
  const iebCourses = courses.filter((c) => isIebTitle(c.title));
  for (const needle of candidates) {
    const hit = iebCourses.find((c) => c.title.toLowerCase().includes(needle));
    if (hit) return { id: hit.id, title: hit.title };
  }
  return null;
};
